/**
 * Content model and copy for the /ai-teammates/marketing SEO page.
 * This page is the marketing-focused subpage in Sprint 1 and targets
 * high-intent terms around autonomous AI agents for marketing teams.
 */

export interface AiTeammatesMarketingSubsection {
	id: string;
	title: string;
	paragraphs: string[];
	orderedList?: string[];
	unorderedList?: string[];
}

export interface AiTeammatesMarketingSection {
	id: string;
	title: string;
	subsections: AiTeammatesMarketingSubsection[];
}

export interface AiTeammatesMarketingFaqItem {
	id: string;
	question: string;
	answer: string;
}

export interface AiTeammatesMarketingInternalLink {
	to: "/ai-teammates" | "/ai-teammates/marketing/google-ads" | "/ai-teammates/marketing/seo";
	label: string;
	description: string;
}

export interface AiTeammatesMarketingExternalLink {
	href: string;
	label: string;
	description: string;
}

export const AI_TEAMMATES_MARKETING_PAGE_TITLE =
	"AI Marketing Agents – SEO, PPC & Email Automation";
export const AI_TEAMMATES_MARKETING_META_DESCRIPTION =
	"Autonomous AI agents that execute SEO audits, Google Ads monitoring, and email-lifecycle workflows—so your marketing team focuses on strategy.";

export const AI_TEAMMATES_MARKETING_H1 = "AI Marketing Agents That Run Your Channels";

export const AI_TEAMMATES_MARKETING_INTRO_PARAGRAPHS = [
	"Marketing operations that run themselves. SEO, PPC, Search Console, and lifecycle workflows — connected and on autopilot.",
	"Most teams already know what to do. The bottleneck is throughput — reports drift, checks slip, opportunities show up too late.",
	"Start with one high-impact workflow. Expand by impact.",
];

export const AI_TEAMMATES_MARKETING_OUTLINE = [
	"Why AI marketing agents matter now",
	"Operating model across PPC, SEO, and analytics",
	"Google Ads and Keyword Planner workflow improvements",
	"Search Console and SEO reporting improvements",
	"Lifecycle and email workflows for automation",
	"Integration coverage and rollout sequencing",
	"Implementation framework for first 30 days",
	"ROI, reliability, and governance controls",
	"Common rollout mistakes and fixes",
	"FAQ and launch checklist",
];

export const AI_TEAMMATES_MARKETING_SECTIONS: AiTeammatesMarketingSection[] = [
	{
		id: "why-ai-marketing-agents",
		title: "Why AI Marketing Agents Matter Right Now",
		subsections: [
			{
				id: "execution-pressure",
				title: "Execution pressure is higher than strategy pressure",
				paragraphs: [
					"Few teams lack channel ideas. Most teams cannot execute those ideas at the speed their market now requires. Media costs shift daily, search demand changes weekly, and stakeholders expect faster reporting cadence than manual processes can support. AI marketing agents create leverage here: they take ownership of repeatable execution loops that currently consume hours without creating direct strategic advantage.",
					"The point is not to automate creativity away. The point is to protect creative and strategic time from operational drag. When campaign checks, trend pulls, and recurring summaries are handled by autonomous teammates, humans spend more time on offer quality, narrative, experimentation design, and decision-making. That shift increases output quality because the team is no longer switching contexts every hour.",
				],
			},
			{
				id: "from-tools-to-operators",
				title: "From isolated tools to operational teammates",
				paragraphs: [
					"Most marketing stacks already include analytics tools, ad platforms, and automation triggers. What they usually lack is a cohesive operating layer that observes events across systems and ships useful actions at the right time. Autonomous AI agents for marketing fill that layer by combining context, instruction, and schedule into one accountable workflow. Instead of manually stitching insights together across tabs, that stitching work goes to a teammate with strict boundaries.",
					"Those boundaries matter. A teammate should have a clear scope, measurable output format, and transparent escalation behavior. Without controls, automation becomes noisy. With controls, automation becomes compounding leverage. Start with one narrow workflow, validate reliability, and expand only after the baseline performs consistently.",
				],
			},
			{
				id: "high-leverage-starts",
				title: "High-leverage starting points",
				paragraphs: [
					"The best starting workflows have three properties: frequent repetition, clear economic impact, and measurable pass-fail quality. Those characteristics allow fast iteration and clear ROI tracking. In practice, the strongest first wins usually come from paid search monitoring, SEO movement summaries, and recurring stakeholder reporting preparation.",
				],
				unorderedList: [
					"Daily spend and anomaly checks for paid campaigns.",
					"Weekly keyword movement and indexing summaries from Search Console.",
					"Recurring channel reports delivered to Slack, email, or dashboards.",
					"Email lifecycle performance reviews with prioritized follow-up actions.",
					"Cross-channel brief creation for leadership updates.",
				],
			},
		],
	},
	{
		id: "autonomous-ai-agents-for-marketing",
		title: "Autonomous AI Agents for Marketing Operations",
		subsections: [
			{
				id: "ppc-google-ads",
				title: "PPC and Google Ads execution loops",
				paragraphs: [
					"For PPC, AI teammates act as a continuous monitoring and synthesis layer. They review campaign-level metrics, identify meaningful changes, and summarize what likely needs human review. This does not replace strategic decisions about budget allocation or creative direction. It removes the repetitive monitoring work that delays those strategic decisions.",
					"An AI teammate for Google Ads should produce consistent outputs with the same structure every run. Required fields like spend delta, conversion movement, top campaigns by volatility, and a short action queue make it easy to compare week-over-week performance, detect risk earlier, and reduce the chance that account issues hide in raw data.",
				],
				orderedList: [
					"Read campaign metrics for a defined time window and compare baselines.",
					"Detect spend, CPA, and conversion anomalies above threshold.",
					"Generate a concise summary with priority flags and next actions.",
					"Route urgent exceptions to the account owner for approval.",
				],
			},
			{
				id: "keyword-planner",
				title: "Keyword Planner and demand intelligence",
				paragraphs: [
					"Keyword research is not a one-time planning task. Search demand, CPC, and intent shift continuously, so it works best as an operating rhythm. With AI teammates, Keyword Planner data can be pulled on schedule, clustered by theme, and translated into practical actions for paid and organic planning. Teams move from occasional research projects to repeatable demand intelligence.",
					"The critical part is interpretation, not extraction. Teammates should separate informational, commercial, and transactional intent patterns, then highlight opportunities where expected value and feasibility align. This keeps keyword work tied to business outcomes instead of turning into long lists that never influence campaign or content decisions.",
				],
				unorderedList: [
					"Track rising keyword groups by intent and expected conversion value.",
					"Flag sudden CPC movement that may impact budget efficiency.",
					"Suggest test clusters for new ad groups and content briefs.",
					"Summarize demand changes for weekly planning meetings.",
				],
			},
			{
				id: "gsc-seo",
				title: "Google Search Console and SEO operations",
				paragraphs: [
					"SEO workflows benefit from consistency more than intensity. Teammates can monitor Search Console trends, detect unexpected losses on high-value pages, and report likely causes with a clear follow-up queue. This prevents critical changes from being discovered too late and helps teams protect momentum when rankings or click-through behavior shift.",
					"AI teammates also make SEO reporting easier to operationalize. Instead of manually rebuilding summaries each week, a teammate produces a stable narrative format with highlights, exceptions, and recommended priorities. That structure turns SEO from a periodic update into a dependable operating signal for the rest of the marketing team.",
				],
				orderedList: [
					"Pull page and query-level trends from a defined comparison period.",
					"Identify high-impact drops and wins above agreed thresholds.",
					"Connect observed shifts to likely causes and confidence levels.",
					"Publish a prioritized action list for editorial and technical owners.",
				],
			},
		],
	},
	{
		id: "email-marketing",
		title: "Email Marketing Workflows That Scale With AI Teammates",
		subsections: [
			{
				id: "lifecycle-automation",
				title: "Lifecycle execution without bottlenecks",
				paragraphs: [
					"Email marketing performance usually depends on operational consistency more than one perfect campaign. Segmentation refreshes, cadence checks, deliverability monitoring, and campaign QA all need recurring attention. AI teammates can own these repeatable workflows so the team spends more time on messaging and less time on maintenance tasks that delay launches.",
					"Lifecycle email works best as a system of loops: triggers, audience hygiene, creative QA, post-send analysis, and iteration. A teammate keeps each loop moving with clear thresholds and escalation rules. That creates better reliability across onboarding, nurture, and reactivation programs without forcing the team into late-night manual checks.",
				],
			},
			{
				id: "deliverability-and-governance",
				title: "Deliverability and governance discipline",
				paragraphs: [
					"Autonomous execution in email requires governance. Hard boundaries around list quality, opt-in standards, unsubscribe handling, and compliance checks should be defined before any automated workflow is scaled. This protects sender reputation and keeps growth sustainable. The right setup makes automation safer because exceptions are identified early and routed to the right owner.",
					"A teammate can monitor bounce spikes, complaint indicators, and engagement shifts as early warning signals. It can also verify required pre-send checks were completed for high-impact campaigns. These controls reduce risk and strengthen confidence when teams scale volume across multiple audience segments.",
				],
				unorderedList: [
					"Track sender health signals and alert on unusual movement.",
					"Verify suppression and unsubscribe handling before major sends.",
					"Enforce standardized campaign QA checklists.",
					"Document escalation decisions for auditability.",
				],
			},
			{
				id: "email-cross-channel",
				title: "Cross-channel follow-up loops",
				paragraphs: [
					"Email should not operate in isolation from paid and organic channels. AI teammates connect campaign outcomes across channels, then suggest practical next actions for segmentation, creative angle, and offer timing. This creates a tighter feedback loop between acquisition performance and lifecycle messaging.",
					"For example, if paid campaigns surface strong intent in a keyword cluster, the teammate can flag relevant lifecycle content opportunities and prepare a short brief for email follow-up. This type of cross-channel orchestration is where autonomous teammates outperform disconnected automations — they synthesize context rather than only trigger one isolated event.",
				],
				orderedList: [
					"Collect channel-level outcomes from paid, SEO, and email systems.",
					"Identify audience segments with meaningful behavior changes.",
					"Draft follow-up recommendations by segment and campaign intent.",
					"Send clear next steps to the owning marketer for approval.",
				],
			},
		],
	},
	{
		id: "integration-coverage",
		title: "Integration Coverage for Marketing Teams",
		subsections: [
			{
				id: "core-integrations",
				title: "Core acquisition and analytics integrations",
				paragraphs: [
					"The core operational stack centers on Google Ads, Keyword Planner, and Google Search Console because those systems drive weekly acquisition decisions for many teams. Connect those first, then add reporting destinations and communication channels. This sequencing keeps complexity manageable and makes value visible quickly.",
					"Once the core signals are stable, the teammate can start handling deeper interpretation and prioritization tasks. The goal is a reliable operating rhythm, not a sprawling integration map on day one. Stability first, then breadth.",
				],
				unorderedList: [
					"Google Ads",
					"Google Keyword Planner",
					"Google Search Console",
					"Google Sheets for recurring reporting outputs",
				],
			},
			{
				id: "expanded-marketing-stack",
				title: "Expanded marketing stack integrations",
				paragraphs: [
					"As workflows mature, expanded integrations improve context depth and execution coverage. Tools like Ahrefs and Adrapid support deeper channel diagnostics, while lifecycle platforms like Brevo, ActiveCampaign, Klaviyo, and Mailchimp support segmentation and campaign operations. Canva supports content production workflows when teams need faster creative iteration tied to performance signals.",
					"Add these integrations in phases based on workflow ownership. If one team owns SEO operations, connect Ahrefs and Search Console first for that stream. If lifecycle marketing is the immediate priority, prioritize Brevo, ActiveCampaign, Klaviyo, and Mailchimp. Phased rollout keeps accountability clear and prevents noisy automation.",
				],
				unorderedList: [
					"Ahrefs",
					"Brevo",
					"ActiveCampaign",
					"Adrapid",
					"Canva",
					"Klaviyo",
					"Mailchimp",
				],
			},
			{
				id: "future-integration-pages",
				title: "How this page supports future integration-specific subpages",
				paragraphs: [
					"This marketing hub is intentionally broad. It explains how autonomous AI marketing workflows operate across channels and tools, then links upward and sideways within the current live architecture. Later, each integration can have a dedicated deep-dive page without fragmenting the core narrative. That structure concentrates relevance while staying easy to expand.",
					"For now, the messaging stays practical and implementation-focused: connect the minimum stack, launch one reliable workflow, and scale deliberately. That foundation makes future integration pages more useful because each can focus on tactical depth rather than repeating category basics.",
				],
			},
		],
	},
	{
		id: "implementation-framework",
		title: "Implementation Framework for the First 30 Days",
		subsections: [
			{
				id: "week-1",
				title: "Week 1: Define one workflow and one owner",
				paragraphs: [
					"Start by choosing one recurring marketing workflow with obvious business impact. Assign one human owner who is accountable for quality and feedback. This creates a clean decision path and avoids the common problem where everyone uses the system but no one owns outcomes.",
					"The first workflow should be narrow and measurable. A daily Google Ads anomaly report or a weekly Search Console movement summary works well. What matters most is that the team can quickly determine whether output is useful or needs refinement.",
				],
			},
			{
				id: "week-2",
				title: "Week 2: Add guardrails and reporting standards",
				paragraphs: [
					"In week two, enforce output standards and escalation logic. Every run should produce a predictable structure so the team can scan decisions quickly. If confidence is low or data is incomplete, the teammate should escalate instead of improvising. These simple rules protect trust while automation volume grows.",
					"Define review cadence early. A 20-minute weekly quality review is usually enough. The goal is to update thresholds, fix recurring misses, and capture learnings in a short changelog so new owners can understand why the workflow behaves a certain way.",
				],
				orderedList: [
					"Set output templates for each run type.",
					"Define escalation conditions and responsible approvers.",
					"Track error causes by category: data, logic, or instruction.",
					"Update instructions with one targeted change at a time.",
				],
			},
			{
				id: "week-3-4",
				title: "Weeks 3-4: Expand to adjacent workflows",
				paragraphs: [
					"Once the first workflow is reliable, expand to adjacent tasks that share data sources or owners. A Google Ads monitoring workflow can pair with Keyword Planner trend synthesis. A Search Console summary can pair with weekly SEO content opportunity briefs. This expansion pattern keeps complexity controlled.",
					"At this point, teams often start seeing compound benefits because channel signals are connected. Reporting becomes faster, prioritization becomes clearer, and escalation becomes more useful. That is when automation feels less like a tool experiment and more like a functioning AI workforce for marketing operations.",
				],
				orderedList: [
					"Add one adjacent workflow per owner, not five at once.",
					"Reuse existing templates and thresholds when possible.",
					"Measure lead-time improvements and time saved.",
					"Scale only after two to three stable cycles.",
				],
			},
		],
	},
	{
		id: "roi-and-risk",
		title: "ROI, Reliability, and Risk Controls",
		subsections: [
			{
				id: "roi-metrics",
				title: "How to measure ROI from AI marketing agents",
				paragraphs: [
					"Measure ROI with outcome metrics tied to cost, speed, and decision quality. Useful examples include reduced manual reporting hours, shorter anomaly detection lead time, improved on-time delivery of channel summaries, and faster handoff from signal detection to approved action. These metrics reflect operational value more clearly than simple run counts.",
					"Track false positive and false negative rates for alerts too. If a teammate floods the team with low-signal warnings, trust falls. If it misses real issues, the system fails its purpose. Managing that balance is core operations work and should be treated with the same rigor as campaign optimization.",
				],
			},
			{
				id: "risk-controls",
				title: "Risk controls that keep automation trustworthy",
				paragraphs: [
					"Trust comes from controls, not confidence language. Least-privilege permissions, explicit escalation behavior, and owner accountability keep workflows safe as volume grows. For sensitive workflows, require approval gates before any impactful action. Read-first workflows are often the cleanest way to start while teams calibrate quality.",
					"Keep operational logs and short postmortems for failed runs. These records make improvements explainable and protect continuity when ownership shifts. Over time, this creates a resilient operating model where autonomous teammates are audited and improved like any other production system.",
				],
				unorderedList: [
					"Least-privilege access to integrations and data scopes.",
					"Approval requirements for high-impact workflow actions.",
					"Structured run logs and issue categorization.",
					"Recurring quality reviews with small iterative updates.",
				],
			},
		],
	},
	{
		id: "mistakes-and-fixes",
		title: "Common Rollout Mistakes and Practical Fixes",
		subsections: [
			{
				id: "mistake-broad-scope",
				title: "Mistake 1: Rolling out too many workflows at once",
				paragraphs: [
					"The most common mistake is teams launching several autonomous workflows in parallel before one is reliable. This creates noisy output, unclear ownership, and weak diagnosis when quality issues show up. The fix: shrink scope immediately and restore one workflow per owner until output stabilizes.",
					"A narrower rollout is not slower in practice. It usually reaches stable value faster because feedback is clear and improvements are easier to isolate. Once one workflow performs reliably, expansion becomes repeatable and confidence grows naturally.",
				],
			},
			{
				id: "mistake-no-review",
				title: "Mistake 2: No review cadence",
				paragraphs: [
					"Automation without review eventually drifts. Short weekly reviews that compare output against success criteria, classify misses, and capture one improvement per workflow keep quality moving up without adding operational overhead.",
					"Without this review loop, teams often misjudge performance because isolated wins hide recurring failures. A lightweight review ritual keeps decision-makers aligned on what is working, what is noisy, and where to focus the next iteration.",
				],
			},
			{
				id: "mistake-misaligned-metrics",
				title: "Mistake 3: Measuring output volume instead of business impact",
				paragraphs: [
					"If success is defined as number of runs or number of alerts, teams optimize for activity instead of outcomes. The fix: tie each workflow to one core business metric and one reliability metric. This dual tracking keeps both value and quality visible at the same time.",
					"When metrics are aligned, decisions become straightforward. Workflows that generate real decisions get expanded. Workflows that generate noise get redesigned or removed. That discipline keeps the AI teammate system focused on results, not novelty.",
				],
				orderedList: [
					"Pick one business metric per workflow before launch.",
					"Add one reliability metric to track quality drift.",
					"Review both metrics weekly with the workflow owner.",
					"Pause or redesign workflows that do not improve outcomes.",
				],
			},
		],
	},
];

export const AI_TEAMMATES_MARKETING_INTEGRATIONS = [
	"Google Ads",
	"Keyword Planner",
	"Google Search Console",
	"Google Sheets",
	"Ahrefs",
	"Brevo",
	"ActiveCampaign",
	"Adrapid",
	"Canva",
	"Klaviyo",
	"Mailchimp",
];

export const AI_TEAMMATES_MARKETING_EXTERNAL_LINKS: AiTeammatesMarketingExternalLink[] = [
	{
		href: "https://www.anthropic.com/research/building-effective-agents",
		label: "Building effective agents — Anthropic",
		description: "Anthropic's guide to building reliable AI agent systems.",
	},
	{
		href: "https://openai.com/index/practices-for-governing-agentic-ai-systems/",
		label: "Practices for governing agentic AI — OpenAI",
		description: "OpenAI's framework for safe AI agent operations.",
	},
	{
		href: "https://cloud.google.com/blog/products/ai-machine-learning/the-next-chapter-of-ai-agents",
		label: "The next chapter of AI agents — Google Cloud",
		description: "Google Cloud's vision for production AI agent infrastructure.",
	},
];

export const AI_TEAMMATES_MARKETING_INTERNAL_LINKS: AiTeammatesMarketingInternalLink[] = [
	{
		to: "/ai-teammates",
		label: "AI teammates hub",
		description: "Read the broader category page and rollout principles.",
	},
	{
		to: "/ai-teammates/marketing/google-ads",
		label: "Google Ads AI teammate page",
		description: "Read the full Google Ads campaign management and automation guide.",
	},
	{
		to: "/ai-teammates/marketing/seo",
		label: "AI SEO teammate page",
		description: "Read the Search Console, Keyword Planner, and SEO reporting workflow breakdown.",
	},
];

export const AI_TEAMMATES_MARKETING_FAQ_ITEMS: AiTeammatesMarketingFaqItem[] = [
	{
		id: "faq-what",
		question: "What can autonomous AI agents for marketing actually do today?",
		answer:
			"They own repeatable workflows like Google Ads monitoring, Keyword Planner trend synthesis, Search Console summaries, and recurring email operations checks. They detect issues early, prepare decision-ready summaries, and keep routine execution on schedule. Humans still own strategy, approvals, and final priorities.",
	},
	{
		id: "faq-ppc",
		question: "Can AI marketing agents manage PPC without losing control?",
		answer:
			"Yes, with clear scope and governance. Start with read-focused monitoring and reporting workflows, then add approval-gated actions only after reliability is proven. Every run should have explicit thresholds, escalation rules, and owner review.",
	},
	{
		id: "faq-seo",
		question: "How do AI teammates help with SEO and Google Search Console?",
		answer:
			"They monitor ranking and click trends, detect meaningful page-level changes, and produce weekly summaries with prioritized follow-ups. This prevents important shifts from being missed and reduces manual reporting effort. The strongest value comes from consistent cadence and structured outputs, not one-off audits.",
	},
	{
		id: "faq-email",
		question: "Do these workflows work for email marketing platforms too?",
		answer:
			"Yes. AI teammates support recurring lifecycle checks, segmentation updates, send QA, and post-campaign summaries across tools like Brevo, ActiveCampaign, Klaviyo, and Mailchimp. Compliance and approval controls stay explicit, especially for high-impact sends.",
	},
	{
		id: "faq-integrations",
		question: "Why include integrations like Ahrefs, Adrapid, and Canva?",
		answer:
			"Each integration adds context where a specific team workflow needs depth. Ahrefs supports search intelligence, Adrapid supports ad operations context, and Canva supports faster creative production workflows tied to channel signals. Add integrations in phases based on clear workflow ownership and measurable outcomes.",
	},
	{
		id: "faq-start",
		question: "What is the best first step for my team this week?",
		answer:
			"Pick one recurring workflow with obvious economic impact, assign one owner, and launch with strict output and escalation rules. Run two to three cycles, review quality, and refine before expanding. Once stable, add adjacent workflows that share the same data sources and owners.",
	},
];

export const AI_TEAMMATES_MARKETING_FAQ_SCHEMA_ITEMS = AI_TEAMMATES_MARKETING_FAQ_ITEMS.map(
	(item) => ({
		question: item.question,
		answer: item.answer,
	})
);
